import { useState, useRef, useEffect } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { 
  FlaskConical, 
  Send, 
  Loader2, 
  Target, 
  Clock,
  DollarSign,
  Brain,
  CheckCircle2,
  User,
  Bot,
  ArrowLeft,
  Plus,
  Calendar,
  CalendarPlus,
  AlertTriangle,
  Info,
  Timer
} from "lucide-react";
import { Link } from "wouter";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Goal, TimeBudget } from "@shared/schema";
import { isGoalVisibleOnMainSurfaces } from "@/lib/goal-visibility";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface PendingMilestone {
  title: string;
  dueDate?: string;
  description?: string;
}

interface PendingTask {
  title: string;
  milestoneIndex?: number;
  priority?: string;
  dueDate?: string;
  description?: string;
}

interface PendingGoalData {
  title: string;
  description?: string;
  targetDate?: string;
  deadline?: string;
  category?: string;
  timeCost?: number;
  timeCostFrequency?: string;
  goalId?: string;
  source: "existing" | "ai-create" | "quick-input";
  milestones?: PendingMilestone[];
  tasks?: PendingTask[];
}

const DAYS_OF_WEEK = [
  { key: "sunday", label: "Sun", fullLabel: "Sunday" },
  { key: "monday", label: "Mon", fullLabel: "Monday" },
  { key: "tuesday", label: "Tue", fullLabel: "Tuesday" },
  { key: "wednesday", label: "Wed", fullLabel: "Wednesday" },
  { key: "thursday", label: "Thu", fullLabel: "Thursday" },
  { key: "friday", label: "Fri", fullLabel: "Friday" },
  { key: "saturday", label: "Sat", fullLabel: "Saturday" }
];

interface TimeBlockingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goalTitle: string;
  suggestedHours?: number;
  onConfirm: (hours: number, selectedDays: string[], timeSlot: string) => void;
  isCreating: boolean;
}

function TimeBlockingDialog({ 
  open, 
  onOpenChange, 
  goalTitle, 
  suggestedHours = 1, 
  onConfirm,
  isCreating 
}: TimeBlockingDialogProps) {
  const [hours, setHours] = useState<number>(suggestedHours);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [timeSlot, setTimeSlot] = useState("09:00");

  useEffect(() => {
    if (suggestedHours && suggestedHours > 0) {
      setHours(suggestedHours);
    }
  }, [suggestedHours]);

  const toggleDay = (day: string) => {
    setSelectedDays(prev => 
      prev.includes(day) 
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleConfirm = () => {
    if (selectedDays.length === 0) return;
    onConfirm(hours, selectedDays, timeSlot);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5" />
            Schedule Time Blocks
          </DialogTitle>
          <DialogDescription>
            Block time on your calendar to work on "{goalTitle}"
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="hours">Hours per session</Label>
            <div className="flex items-center gap-2">
              <Input
                id="hours"
                type="number"
                min={0.5}
                max={8}
                step={0.5}
                value={hours}
                onChange={(e) => setHours(parseFloat(e.target.value) || 1)}
                className="w-24"
                data-testid="input-time-block-hours"
              />
              <span className="text-sm text-muted-foreground">hours</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Select days of the week</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OF_WEEK.map((day) => (
                <Button
                  key={day.key}
                  type="button"
                  variant={selectedDays.includes(day.key) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleDay(day.key)}
                  className="w-12"
                  data-testid={`button-day-${day.key}`}
                >
                  {day.label}
                </Button>
              ))}
            </div>
            {selectedDays.length === 0 && (
              <p className="text-xs text-muted-foreground">Select at least one day</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="time">Start time</Label>
            <Input
              id="time"
              type="time"
              value={timeSlot}
              onChange={(e) => setTimeSlot(e.target.value)}
              className="w-32"
              data-testid="input-time-slot"
            />
          </div>

          <div className="p-3 rounded-lg bg-muted/50 text-sm">
            <p className="font-medium mb-1">Preview:</p>
            {selectedDays.length > 0 ? (
              <p className="text-muted-foreground">
                {hours} hour{hours !== 1 ? 's' : ''} on {selectedDays.map(d => 
                  DAYS_OF_WEEK.find(day => day.key === d)?.fullLabel
                ).join(', ')} at {timeSlot}
              </p>
            ) : (
              <p className="text-muted-foreground">No days selected</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={selectedDays.length === 0 || isCreating}
            data-testid="button-confirm-time-blocks"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CalendarPlus className="w-4 h-4 mr-2" />
                Add to Calendar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GoalSimulationEmptyState({ setLocation }: { setLocation: (path: string) => void }) {
  const [selectedGoalId, setSelectedGoalId] = useState<string>("");
  const [quickGoalTitle, setQuickGoalTitle] = useState("");
  
  const { data: goals, isLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  const currentGoals = goals?.filter(isGoalVisibleOnMainSurfaces) ?? [];

  const handleStartSimulation = () => {
    const goal = currentGoals.find(g => g.id === selectedGoalId);
    if (!goal) return;
    
    const goalData: PendingGoalData = { 
      title: goal.title,
      description: goal.description || undefined,
      targetDate: goal.deadline ? goal.deadline.toString() : undefined,
      category: goal.category || undefined,
      timeCost: goal.timeCost || undefined,
      timeCostFrequency: goal.timeCostFrequency || undefined,
      goalId: goal.id,
      source: "existing"
    };
    sessionStorage.setItem('pendingGoalSimulation', JSON.stringify(goalData));
    window.location.reload();
  };

  const handleQuickSimulation = () => {
    if (!quickGoalTitle.trim()) return;
    
    const goalData: PendingGoalData = {
      title: quickGoalTitle.trim(),
      source: "quick-input"
    };
    sessionStorage.setItem('pendingGoalSimulation', JSON.stringify(goalData));
    window.location.reload();
  };

  return (
    <div className="w-full max-w-full py-4 md:py-6 px-4 md:px-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5" />
            Goal Simulation
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            Analyze a goal to understand the true cost in time, money, and trade-offs before committing.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Quick simulation - enter a goal idea</label>
              <div className="flex gap-2">
                <Input
                  value={quickGoalTitle}
                  onChange={(e) => setQuickGoalTitle(e.target.value)}
                  placeholder="e.g., Learn to play guitar, Run a marathon..."
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickSimulation()}
                  data-testid="input-quick-goal"
                />
                <Button 
                  onClick={handleQuickSimulation}
                  disabled={!quickGoalTitle.trim()}
                  data-testid="button-quick-simulate"
                >
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Simulate
                </Button>
              </div>
            </div>
          </div>
          
          <Separator />
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : currentGoals.length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Or select an existing goal to analyze</label>
                <Select value={selectedGoalId} onValueChange={setSelectedGoalId}>
                  <SelectTrigger className="w-full" data-testid="select-goal-trigger">
                    <SelectValue placeholder="Choose a goal..." />
                  </SelectTrigger>
                  <SelectContent>
                    {currentGoals.map((goal) => (
                      <SelectItem key={goal.id} value={goal.id} data-testid={`select-goal-${goal.id}`}>
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-primary" />
                          <span>{goal.title}</span>
                          {goal.category && (
                            <Badge variant="secondary" className="text-xs ml-2">
                              {goal.category}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedGoalId && (
                <div className="p-4 rounded-lg bg-muted/50 border">
                  {(() => {
                    const goal = currentGoals.find(g => g.id === selectedGoalId);
                    if (!goal) return null;
                    return (
                      <div className="space-y-2">
                        <h3 className="font-medium">{goal.title}</h3>
                        {goal.description && (
                          <p className="text-sm text-muted-foreground">{goal.description}</p>
                        )}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {goal.deadline && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(goal.deadline).toLocaleDateString()}
                            </span>
                          )}
                          <span>{goal.progress || 0}% complete</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              <Button 
                onClick={handleStartSimulation} 
                disabled={!selectedGoalId}
                className="w-full"
                data-testid="button-start-simulation"
              >
                <FlaskConical className="w-4 h-4 mr-2" />
                Analyze This Goal
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                No goals found. Enter a goal idea above or create one first.
              </p>
              <Button onClick={() => setLocation("/goals")} data-testid="button-go-to-goals">
                <Plus className="w-4 h-4 mr-2" />
                Create a Goal
              </Button>
            </div>
          )}
          
          <Separator />
          
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-2">
              Or create a new goal through guided AI conversation
            </p>
            <Button variant="outline" size="sm" onClick={() => setLocation("/ai-create")} data-testid="button-go-to-ai-create">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go to AI Create
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface SimulationState {
  step: "analyzing" | "comparing" | "discussing" | "finalizing";
  proposedGoal: {
    title: string;
    description?: string;
    targetDate?: string;
    category?: string;
    estimatedTimePerWeek?: number;
    estimatedCost?: number;
  } | null;
  analysis: {
    totalExistingTimeCommitment?: number;
    totalExistingCost?: number;
    availableTime?: number;
    conflicts?: string[];
    recommendations?: string[];
  } | null;
  timeBudgetUsed?: boolean;
  timeBudgetName?: string | null;
  availableMinutesFromBudget?: number | null;
}

export default function GoalSimulationPage() {
  // Read sessionStorage BEFORE any hooks to avoid temporal dead zone issues in production builds
  let proposedGoal: PendingGoalData | null = null;
  try {
    const pendingGoalData = sessionStorage.getItem('pendingGoalSimulation');
    proposedGoal = pendingGoalData ? JSON.parse(pendingGoalData) : null;
  } catch {
    sessionStorage.removeItem('pendingGoalSimulation');
  }

  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [simulationState, setSimulationState] = useState<SimulationState>({
    step: "analyzing",
    proposedGoal: null,
    analysis: null
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [showTimeBlockingDialog, setShowTimeBlockingDialog] = useState(false);
  const [createdGoalId, setCreatedGoalId] = useState<string | null>(null);

  // Editable local copies of milestones/tasks — updated when AI suggests changes during simulation
  const [localMilestones, setLocalMilestones] = useState<PendingMilestone[]>(
    () => proposedGoal?.milestones ?? []
  );
  const [localTasks, setLocalTasks] = useState<PendingTask[]>(
    () => proposedGoal?.tasks ?? []
  );

  const { data: existingGoals, isLoading: goalsLoading } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: activeTimeBudget } = useQuery<TimeBudget>({
    queryKey: ["/api/time-budgets/active"],
  });
  
  const isExistingGoal = proposedGoal?.source === "existing";
  const needsCreation = proposedGoal?.source === "ai-create" || proposedGoal?.source === "quick-input" || !proposedGoal?.source;

  useEffect(() => {
    if (!isInitialized && existingGoals && proposedGoal) {
      setSimulationState(prev => ({
        ...prev,
        proposedGoal
      }));
      
      const sourceText = isExistingGoal 
        ? "I'll analyze your existing goal" 
        : "I'll help you analyze this proposed goal";
      
      const welcomeMessage: Message = {
        id: "welcome",
        role: "assistant",
        content: `Welcome to Goal Simulation! ${sourceText}: **"${proposedGoal.title}"**

I'll help you understand the true cost of this goal in terms of:
- **Time commitment** required each week
- **Financial investment** if applicable
- **Trade-offs** with your existing ${existingGoals.length} goals
- **Resource allocation** and attention needed

Let me start by analyzing your current goals and commitments...`,
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
      setIsInitialized(true);
      
      setTimeout(() => {
        startAnalysisMutation.mutate({});
      }, 1500);
    }
  }, [existingGoals, proposedGoal, isInitialized]);

  const startAnalysisMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/goal-simulation/analyze", {
        proposedGoal: simulationState.proposedGoal || proposedGoal,
        existingGoals: existingGoals?.filter(isGoalVisibleOnMainSurfaces).map(g => ({
          id: g.id,
          title: g.title,
          description: g.description,
          deadline: g.deadline,
          progress: g.progress,
          category: g.category,
          timeCost: g.timeCost,
          timeCostFrequency: g.timeCostFrequency
        }))
      });
      return response.json();
    },
    onSuccess: (data) => {
      setSimulationState(prev => ({
        ...prev,
        step: "comparing",
        analysis: data.analysis,
        timeBudgetUsed: data.timeBudgetUsed || false,
        timeBudgetName: data.timeBudgetName || null,
        availableMinutesFromBudget: data.availableMinutesFromBudget ?? null
      }));
      
      const analysisMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, analysisMessage]);
    },
    onError: (error: Error) => {
      toast({
        title: "Analysis Error",
        description: error.message || "Failed to analyze goal",
        variant: "destructive"
      });
    }
  });

  const sendMessageMutation = useMutation({
    mutationFn: async (userMessage: string) => {
      const response = await apiRequest("POST", "/api/goal-simulation/chat", {
        message: userMessage,
        simulationState,
        proposedGoal: simulationState.proposedGoal || proposedGoal,
        existingGoals: existingGoals?.filter(isGoalVisibleOnMainSurfaces).map(g => ({
          id: g.id,
          title: g.title,
          description: g.description,
          deadline: g.deadline,
          progress: g.progress,
          category: g.category,
          timeCost: g.timeCost,
          timeCostFrequency: g.timeCostFrequency
        })),
        messageHistory: messages.map(m => ({ role: m.role, content: m.content }))
      });
      return response.json();
    },
    onSuccess: (data) => {
      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: data.response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (data.updatedGoal) {
        setSimulationState(prev => ({
          ...prev,
          proposedGoal: { ...prev.proposedGoal, ...data.updatedGoal }
        }));
      }

      if (data.updatedMilestones) {
        setLocalMilestones(data.updatedMilestones);
      }

      if (data.updatedTasks) {
        setLocalTasks(data.updatedTasks);
      }
      
      if (data.newStep) {
        setSimulationState(prev => ({
          ...prev,
          step: data.newStep
        }));
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to get AI response",
        variant: "destructive"
      });
    }
  });

  const createGoalMutation = useMutation({
    mutationFn: async () => {
      const goalData = simulationState.proposedGoal || proposedGoal;
      const hasMilestones = (proposedGoal?.milestones?.length ?? 0) > 0;

      // When coming from AI Create with milestones/tasks, use the full creation endpoint
      if (proposedGoal?.source === "ai-create" && hasMilestones) {
        const response = await apiRequest("POST", "/api/ai-create/create-goal-full", {
          title: goalData?.title,
          description: goalData?.description || null,
          deadline: goalData?.targetDate || proposedGoal?.deadline || null,
          category: goalData?.category || proposedGoal?.category || '',
          progress: 0,
          timeCost: proposedGoal?.timeCost || null,
          timeCostFrequency: proposedGoal?.timeCostFrequency || null,
          milestones: proposedGoal.milestones,
          tasks: proposedGoal.tasks || [],
        });
        return response.json();
      }

      // Standard creation for quick-input / existing goal analysis
      const response = await apiRequest("POST", "/api/goals", {
        title: goalData?.title,
        description: goalData?.description || null,
        deadline: goalData?.targetDate ? new Date(goalData.targetDate) : null,
        category: goalData?.category || '',
        progress: 0,
        timeCost: proposedGoal?.timeCost || null,
        timeCostFrequency: proposedGoal?.timeCostFrequency || null
      });
      return response.json();
    },
    onSuccess: (data) => {
      // create-goal-full returns { goal, milestones, tasks }, basic returns the goal directly
      const goal = data.goal || data;
      setCreatedGoalId(goal.id);
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });

      const mCount = data.milestones?.length ?? 0;
      const tCount = data.tasks?.length ?? 0;
      const detail = mCount > 0
        ? ` with ${mCount} milestone${mCount !== 1 ? 's' : ''}${tCount > 0 ? ` and ${tCount} task${tCount !== 1 ? 's' : ''}` : ''}`
        : '';

      toast({
        title: "Goal Created!",
        description: `Your goal "${goal.title}" has been added${detail}.`
      });

      const successMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `Excellent! Your goal **"${goal.title}"** has been created successfully!${detail ? `\n\nCreated${detail}.` : ''}

Would you like to **schedule time blocks** on your calendar to work on this goal? This helps ensure you dedicate focused time to achieving it.

Click the "Schedule Time Blocks" button below to add recurring time blocks to your calendar.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMessage]);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create goal",
        variant: "destructive"
      });
    }
  });

  const createTimeBlocksMutation = useMutation({
    mutationFn: async ({ hours, selectedDays, timeSlot }: { hours: number, selectedDays: string[], timeSlot: string }) => {
      const goalData = simulationState.proposedGoal || proposedGoal;
      const goalId = createdGoalId || proposedGoal?.goalId;
      
      const [startHour, startMinute] = timeSlot.split(':').map(Number);
      const endHour = startHour + Math.floor(hours);
      const endMinute = startMinute + (hours % 1) * 60;
      
      const dayMap: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
        thursday: 4, friday: 5, saturday: 6
      };
      
      const createdEvents = [];
      const today = new Date();
      
      const endRecurrenceDate = proposedGoal?.targetDate 
        ? new Date(proposedGoal.targetDate) 
        : new Date(today.getFullYear(), today.getMonth() + 3, today.getDate());
      
      for (const dayKey of selectedDays) {
        const targetDay = dayMap[dayKey];
        const currentDay = today.getDay();
        let daysUntil = targetDay - currentDay;
        if (daysUntil <= 0) daysUntil += 7;
        
        const eventDate = new Date(today);
        eventDate.setDate(today.getDate() + daysUntil);
        eventDate.setHours(startHour, startMinute, 0, 0);
        
        const endDate = new Date(eventDate);
        endDate.setHours(endHour, Math.floor(endMinute), 0, 0);
        
        const response = await apiRequest("POST", "/api/calendar", {
          title: `Work on: ${goalData?.title}`,
          description: `Weekly time block for goal: ${goalData?.title}`,
          startTime: eventDate.toISOString(),
          endTime: endDate.toISOString(),
          color: "#3b82f6",
          goalId: goalId || undefined,
          isRecurring: true,
          recurrenceFrequency: "weekly",
          recurrenceEndDate: endRecurrenceDate.toISOString()
        });
        
        createdEvents.push(await response.json());
      }
      
      return { events: createdEvents, selectedDays };
    },
    onSuccess: (data) => {
      setShowTimeBlockingDialog(false);
      queryClient.invalidateQueries({ queryKey: ["/api/calendar"] });
      const dayCount = data.selectedDays.length;
      toast({
        title: "Weekly Time Blocks Created!",
        description: `Added ${dayCount} recurring weekly time block${dayCount !== 1 ? 's' : ''} to your calendar.`
      });
      
      const dayLabels = data.selectedDays.map(d => 
        DAYS_OF_WEEK.find(day => day.key === d)?.fullLabel
      ).join(', ');
      
      const successMessage: Message = {
        id: Date.now().toString(),
        role: "assistant",
        content: `I've added **${dayCount} recurring weekly time block${dayCount !== 1 ? 's' : ''}** to your calendar! 📅

Your time blocks are scheduled for every **${dayLabels}** and will repeat weekly until your goal deadline.

You can view and manage these events in your Calendar. Consistent time blocks will help you make steady progress toward your goal.

Would you like to do anything else, or are you ready to start working toward your goal?`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMessage]);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create time blocks",
        variant: "destructive"
      });
    }
  });

  const handleSendMessage = () => {
    if (!inputValue.trim() || sendMessageMutation.isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    sendMessageMutation.mutate(inputValue.trim());
    setInputValue("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateGoal = () => {
    createGoalMutation.mutate();
  };

  const handleFinishSimulation = () => {
    sessionStorage.removeItem('pendingGoalSimulation');
    if (isExistingGoal) {
      setLocation("/goals");
    } else if (createdGoalId) {
      setLocation("/goals");
    } else {
      setLocation("/goals");
    }
  };

  const handleTimeBlockConfirm = (hours: number, selectedDays: string[], timeSlot: string) => {
    createTimeBlocksMutation.mutate({ hours, selectedDays, timeSlot });
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  if (goalsLoading) {
    return (
      <div className="w-full max-w-full py-4 md:py-6 px-4 md:px-6 flex items-center justify-center h-[80vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!proposedGoal) {
    return <GoalSimulationEmptyState setLocation={setLocation} />;
  }

  const showCreateButton = needsCreation && !createdGoalId;
  const showTimeBlockButton = (isExistingGoal || createdGoalId) && simulationState.step !== "analyzing";

  return (
    <div className="w-full max-w-full py-4 md:py-6 px-4 md:px-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 h-[calc(100vh-8rem)]">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <FlaskConical className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl flex items-center gap-2">
                    <FlaskConical className="w-5 h-5" />
                    Goal Simulation
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isExistingGoal ? "Analyzing your existing goal" : "Analyzing proposed goal"}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFinishSimulation}
                data-testid="button-finish-simulation"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Done
              </Button>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col h-[calc(100%-5rem)]">
            <ScrollArea 
              className="flex-1 pr-4 mb-4"
              data-testid="simulation-chat-messages"
            >
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {message.role === "assistant" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          <Bot className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                      data-testid={`simulation-message-${message.role}-${message.id}`}
                    >
                      <div className="whitespace-pre-wrap text-sm prose prose-sm dark:prose-invert max-w-none">
                        {message.content.split('\n').map((line, i) => {
                          if (line.startsWith('**') && line.endsWith('**')) {
                            return <p key={i} className="font-semibold my-1">{line.replace(/\*\*/g, '')}</p>;
                          }
                          if (line.match(/^\*\*.*\*\*/)) {
                            const parts = line.split(/(\*\*.*?\*\*)/g);
                            return (
                              <p key={i} className="my-1">
                                {parts.map((part, j) => 
                                  part.startsWith('**') && part.endsWith('**') 
                                    ? <strong key={j}>{part.replace(/\*\*/g, '')}</strong>
                                    : part
                                )}
                              </p>
                            );
                          }
                          if (line.startsWith('- ')) {
                            return <p key={i} className="my-1 ml-2">{line}</p>;
                          }
                          return <p key={i} className="my-1">{line}</p>;
                        })}
                      </div>
                    </div>
                    {message.role === "user" && (
                      <Avatar className="w-8 h-8 shrink-0">
                        <AvatarFallback className="bg-secondary">
                          <User className="w-4 h-4" />
                        </AvatarFallback>
                      </Avatar>
                    )}
                  </div>
                ))}
                {(sendMessageMutation.isPending || startAnalysisMutation.isPending) && (
                  <div className="flex gap-3 justify-start">
                    <Avatar className="w-8 h-8 shrink-0">
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        <Bot className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="bg-muted rounded-lg p-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            <div className="space-y-3">
              {messages.length > 0 && simulationState.step !== "analyzing" && (
                <div className="flex gap-2 flex-wrap">
                  {showCreateButton && (
                    <Button
                      onClick={handleCreateGoal}
                      disabled={createGoalMutation.isPending || startAnalysisMutation.isPending || sendMessageMutation.isPending}
                      className="flex-1"
                      data-testid="button-create-goal"
                    >
                      {createGoalMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Target className="w-4 h-4 mr-2" />
                      )}
                      Create This Goal
                    </Button>
                  )}
                  {showTimeBlockButton && (
                    <Button
                      variant="outline"
                      onClick={() => setShowTimeBlockingDialog(true)}
                      disabled={createTimeBlocksMutation.isPending}
                      className="flex-1"
                      data-testid="button-schedule-time-blocks"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Time Blocks
                    </Button>
                  )}
                  {proposedGoal?.source === "ai-create" && !createdGoalId && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // Write updated milestones/tasks back to sessionStorage so
                        // AI Create can restore the confirmation panel with the latest plan
                        const updatedData = {
                          ...proposedGoal,
                          milestones: localMilestones,
                          tasks: localTasks,
                          ...(simulationState.proposedGoal ? {
                            title: simulationState.proposedGoal.title ?? proposedGoal.title,
                            description: simulationState.proposedGoal.description ?? proposedGoal.description,
                          } : {}),
                        };
                        sessionStorage.setItem('pendingGoalSimulation', JSON.stringify(updatedData));
                        setLocation("/ai-create");
                      }}
                      data-testid="button-back-to-review"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Review
                    </Button>
                  )}
                </div>
              )}
              
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask about time, resources, or trade-offs..."
                  disabled={sendMessageMutation.isPending || startAnalysisMutation.isPending}
                  className="flex-1"
                  data-testid="input-simulation-message"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || sendMessageMutation.isPending || startAnalysisMutation.isPending}
                  data-testid="button-send-simulation-message"
                >
                  {sendMessageMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-4 h-4" />
                {isExistingGoal ? "Your Goal" : "Proposed Goal"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold mb-2">{proposedGoal.title}</h3>
              {proposedGoal.description && (
                <p className="text-sm text-muted-foreground mb-3">{proposedGoal.description}</p>
              )}
              <div className="space-y-2 text-sm">
                {isExistingGoal && (
                  <Badge variant="secondary" className="text-xs">
                    Existing Goal
                  </Badge>
                )}
                {proposedGoal.targetDate && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Target: {new Date(proposedGoal.targetDate).toLocaleDateString()}</span>
                  </div>
                )}
                {proposedGoal.timeCost && proposedGoal.timeCostFrequency && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{proposedGoal.timeCost}h/{proposedGoal.timeCostFrequency}</span>
                  </div>
                )}
                {simulationState.proposedGoal?.estimatedTimePerWeek && !proposedGoal.timeCost && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{simulationState.proposedGoal.estimatedTimePerWeek}h/week (estimated)</span>
                  </div>
                )}
                {simulationState.proposedGoal?.estimatedCost && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="w-4 h-4" />
                    <span>${simulationState.proposedGoal.estimatedCost}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {simulationState.analysis && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  Analysis Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {simulationState.timeBudgetUsed ? (
                  <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                    <Timer className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      Using your Time Budget "{simulationState.timeBudgetName}" for accurate analysis
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
                    <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <AlertDescription className="text-amber-800 dark:text-amber-200">
                      No Time Budget found. 
                      <Link href="/time-budget" className="underline ml-1 font-medium" data-testid="link-create-time-budget">
                        Create one
                      </Link>
                      {" "}for more accurate time analysis.
                    </AlertDescription>
                  </Alert>
                )}
                
                {simulationState.timeBudgetUsed && simulationState.availableMinutesFromBudget != null && (
                  <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                    <p className="text-xs text-muted-foreground mb-1">Available Hours from Time Budget</p>
                    <p className="font-bold text-lg text-primary" data-testid="text-available-hours-budget">
                      {Math.round(simulationState.availableMinutesFromBudget / 60 * 10) / 10}h/week
                    </p>
                  </div>
                )}
                
                {simulationState.analysis.totalExistingTimeCommitment !== undefined && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Current Time Commitment</p>
                    <p className="font-semibold">{simulationState.analysis.totalExistingTimeCommitment}h/week</p>
                  </div>
                )}
                {simulationState.analysis.availableTime !== undefined && !simulationState.timeBudgetUsed && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Available Time (estimated)</p>
                    <p className="font-semibold">{simulationState.analysis.availableTime}h/week</p>
                  </div>
                )}
                
                {simulationState.timeBudgetUsed && 
                 simulationState.availableMinutesFromBudget != null && 
                 simulationState.analysis.totalExistingTimeCommitment !== undefined &&
                 (simulationState.analysis.totalExistingTimeCommitment > (simulationState.availableMinutesFromBudget / 60)) && (
                  <Alert variant="destructive" className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Warning:</strong> Your goals exceed your available time budget!
                    </AlertDescription>
                  </Alert>
                )}
                
                {simulationState.analysis.conflicts && simulationState.analysis.conflicts.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Potential Conflicts</p>
                    <div className="space-y-1">
                      {simulationState.analysis.conflicts.map((conflict, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {conflict}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {existingGoals && existingGoals.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Your Current Goals ({existingGoals.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {existingGoals.map((goal) => (
                      <div key={goal.id} className="p-2 rounded border text-sm">
                        <p className="font-medium truncate">{goal.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-primary rounded-full" 
                              style={{ width: `${goal.progress || 0}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{goal.progress || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <TimeBlockingDialog
        open={showTimeBlockingDialog}
        onOpenChange={setShowTimeBlockingDialog}
        goalTitle={proposedGoal?.title || ""}
        suggestedHours={proposedGoal?.timeCost ? proposedGoal.timeCost / 60 : simulationState.proposedGoal?.estimatedTimePerWeek ? Math.ceil(simulationState.proposedGoal.estimatedTimePerWeek / 5) : 1}
        onConfirm={handleTimeBlockConfirm}
        isCreating={createTimeBlocksMutation.isPending}
      />
    </div>
  );
}
